import React, { useState, useEffect } from 'react';
import { Link2, Check, AlertCircle, RefreshCw } from 'lucide-react';

const SlugEditor = ({
  title = '',
  currentSlug = '',
  type = 'pillar',
  pillarSlug = '',
  onChange,
  existingSlugs = [],
}) => {
  const [slug, setSlug] = useState(currentSlug);
  const [isValid, setIsValid] = useState(true);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    setSlug(currentSlug);
  }, [currentSlug]);

  const generateSlugFromTitle = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^\w\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/--+/g, '-') // Replace multiple dashes
      .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
      .slice(0, 60);
  };

  const validateSlug = (value) => {
    if (!value) {
      setIsValid(false);
      setValidationMessage('Le slug est requis');
      return;
    }

    // Check format
    if (!/^[a-z0-9-]+$/.test(value)) {
      setIsValid(false);
      setValidationMessage('Utilisez uniquement des lettres minuscules, chiffres et tirets');
      return;
    }

    // Check length
    if (value.length > 60) {
      setIsValid(false);
      setValidationMessage('Le slug ne doit pas dépasser 60 caractères');
      return;
    }

    // Check duplicates
    if (existingSlugs.includes(value)) {
      setIsValid(false);
      setIsDuplicate(true);
      setValidationMessage('Ce slug est déjà utilisé');
      return;
    }

    setIsValid(true);
    setIsDuplicate(false);
    setValidationMessage('');
  };

  // Validate slug when it changes
  useEffect(() => {
    validateSlug(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, existingSlugs]);

  const handleSlugChange = (value) => {
    // Clean automatically while typing
    const cleanedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/--+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    setSlug(cleanedSlug);
    onChange(cleanedSlug);
  };

  const handleRegenerate = () => {
    const newSlug = generateSlugFromTitle(title);
    setSlug(newSlug);
    onChange(newSlug);
  };

  const generateFullUrl = () => {
    const baseUrl = 'https://aidatacleaner.xyz/blog';
    
    if (type === 'pillar') {
      return `${baseUrl}/${slug || 'votre-slug'}`;
    }
    
    if (pillarSlug) {
      return `${baseUrl}/${pillarSlug}/${slug || 'votre-slug'}`;
    }
    
    return `${baseUrl}/[page-pilier]/${slug || 'votre-slug'}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Slug URL *
        </label>
        <button
          type="button"
          onClick={handleRegenerate}
          disabled={!title}
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          data-testid="regenerate-slug-btn"
        >
          <RefreshCw size={12} />
          Régénérer depuis le titre
        </button>
      </div>

      <div className="relative">
        <input
          type="text"
          value={slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          placeholder="url-friendly-slug"
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
            slug
              ? isValid
                ? 'border-green-300 bg-green-50'
                : 'border-red-300 bg-red-50'
              : 'border-gray-300'
          }`}
          data-testid="slug-input"
        />
        {slug && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isValid ? (
              <Check size={18} className="text-green-600" />
            ) : (
              <AlertCircle size={18} className="text-red-600" />
            )}
          </div>
        )}
      </div>

      {/* Validation message */}
      {validationMessage && (
        <div className={`flex items-center gap-2 text-xs ${
          isDuplicate ? 'text-red-600' : 'text-gray-600'
        }`}>
          <AlertCircle size={14} />
          {validationMessage}
        </div>
      )}

      {/* Slug rules */}
      {!slug && (
        <div className="text-xs text-gray-500 space-y-1">
          <div>Règles pour un bon slug :</div>
          <ul className="list-disc list-inside space-y-0.5 ml-2">
            <li>Lettres minuscules, chiffres et tirets uniquement</li>
            <li>Maximum 60 caractères</li>
            <li>Descriptif et contenant des mots-clés</li>
            <li>Éviter les mots vides (le, la, de, etc.)</li>
          </ul>
        </div>
      )}

      {/* URL Preview */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <Link2 size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm flex-1 min-w-0">
          <div className="font-medium text-blue-900 mb-1">URL finale :</div>
          <div className={`break-all font-mono text-xs ${
            isValid ? 'text-blue-700' : 'text-gray-500'
          }`}>
            {generateFullUrl()}
          </div>
          <div className="text-xs text-blue-600 mt-2">
            {slug ? `Longueur : ${slug.length}/60 caractères` : 'Entrez un slug'}
          </div>
        </div>
      </div>

      {/* SEO Tips */}
      {isValid && slug && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-green-800">
              <div className="font-medium mb-1">✓ Slug valide et optimisé SEO</div>
              <div>Ce slug est court, descriptif et facile à partager.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlugEditor;
